import { useQuery } from "@tanstack/react-query";
import { toPng } from "html-to-image";
import {
  Building,
  ChevronDown,
  ChevronRight,
  Download,
  UserCircle2,
  UserMinus,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Badge } from "../components/Badge";
import { ErrorPanel, Loading } from "../components/Loading";
import { PageHeader } from "../components/ui/PageHeader";
import { api } from "../lib/api";
import type { OrgChartNode } from "../lib/api";
import { useLocale } from "../lib/i18n";
import { useLegalEntityGate } from "../lib/legalEntity";

// Org Chart (M10.5 → completed in M21.6). Renders the Position tree
// from GET /api/org-chart with:
//   - CSS connector lines between a parent and its children (the
//     classic org-chart "└─" elbows — no SVG layout engine needed,
//     reliable in print + PNG export);
//   - clickable nodes: the position card links to Position Detail,
//     the worker chip links to Employee Detail;
//   - a Department filter that dims positions outside the chosen dept;
//   - "Export PNG" via html-to-image.

function statusTone(s: string): "green" | "amber" | "slate" | "rose" {
  switch (s) {
    case "filled":
      return "green";
    case "open":
      return "amber";
    case "draft":
      return "slate";
    case "abolished":
      return "rose";
    default:
      return "slate";
  }
}

function Node({
  node,
  deptFilter,
}: {
  node: OrgChartNode;
  deptFilter: string;
}) {
  const [open, setOpen] = useState(true);
  const hasChildren = node.children.length > 0;
  const { locale } = useLocale();
  const { t } = useTranslation();

  const title = locale === "ar" ? node.title_ar : node.title_en;
  const assigneeName =
    node.current_assignee &&
    (locale === "ar"
      ? `${node.current_assignee.first_name_ar} ${node.current_assignee.last_name_ar}`
      : `${node.current_assignee.first_name_en} ${node.current_assignee.last_name_en}`);

  // Dim positions outside the selected department. The whole subtree
  // still renders (so reporting lines stay intact) — non-matching
  // nodes just fade back.
  const dimmed =
    deptFilter !== "" && node.department_name_en !== deptFilter;

  return (
    <li className="org-node">
      <div className={dimmed ? "opacity-35 transition-opacity" : ""}>
        <div className="org-card flex items-start gap-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <button
            type="button"
            aria-label={open ? t("org_chart.collapse") : t("org_chart.expand")}
            onClick={() => setOpen(!open)}
            disabled={!hasChildren}
            className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-slate-400 ${
              hasChildren
                ? "hover:bg-slate-100 hover:text-slate-700"
                : "opacity-30"
            }`}
          >
            {open ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-slate-500">
              <span className="tabular-nums">
                {node.position_number_display}
              </span>
              {node.department_name_en && (
                <span className="inline-flex items-center gap-1">
                  <Building className="h-3 w-3" />
                  {node.department_name_en}
                </span>
              )}
              <Badge tone={statusTone(node.status)}>{node.status}</Badge>
            </div>
            <Link
              to={`/people/positions/${node.id}`}
              className="mt-1 block text-sm font-semibold text-slate-900 hover:underline"
            >
              {title}
            </Link>
            <div className="mt-1.5 text-sm">
              {node.current_assignee ? (
                <Link
                  to={`/people/employees/${node.current_assignee.id}`}
                  className="inline-flex items-center gap-1.5 text-slate-600 hover:underline"
                >
                  <UserCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium">{assigneeName}</span>
                  <span className="text-slate-400">
                    ({node.current_assignee.employee_code})
                  </span>
                </Link>
              ) : (
                <span className="inline-flex items-center gap-1.5 italic text-slate-500">
                  <UserMinus className="h-4 w-4 text-amber-500" />
                  {t("org_chart.vacant")}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {hasChildren && open && (
        <ul className="org-children">
          {node.children.map((child) => (
            <Node key={child.id} node={child} deptFilter={deptFilter} />
          ))}
        </ul>
      )}
    </li>
  );
}

function _collectDepartments(nodes: OrgChartNode[]): string[] {
  const seen = new Set<string>();
  const walk = (n: OrgChartNode) => {
    if (n.department_name_en) seen.add(n.department_name_en);
    n.children.forEach(walk);
  };
  nodes.forEach(walk);
  return [...seen].sort();
}

export function OrgChartPage() {
  const { t } = useTranslation();
  const { legalEntityId: le, gate } = useLegalEntityGate();
  const [deptFilter, setDeptFilter] = useState("");
  const [exporting, setExporting] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["org-chart", le],
    queryFn: () => api.getOrgChart(le),
    enabled: !!le,
  });

  const departments = useMemo(
    () => (data ? _collectDepartments(data.nodes) : []),
    [data],
  );

  const exportPng = async () => {
    if (!chartRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(chartRef.current, {
        backgroundColor: "#ffffff",
        pixelRatio: 2,
      });
      const link = document.createElement("a");
      link.download = `org-chart-${new Date()
        .toISOString()
        .slice(0, 10)}.png`;
      link.href = dataUrl;
      link.click();
    } finally {
      setExporting(false);
    }
  };

  if (gate) return gate;
  if (isLoading) return <Loading />;
  if (error) return <ErrorPanel message={(error as Error).message} />;
  if (!data) return null;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t("org_chart.page_title")}
        subtitle={`(${data.hierarchy_type})`}
      />
      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          {departments.length > 0 && (
            <select
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
            >
              <option value="">{t("org_chart.all_departments")}</option>
              {departments.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={exportPng}
            disabled={exporting || data.nodes.length === 0}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {exporting
              ? t("org_chart.exporting")
              : t("org_chart.export_png")}
          </button>
        </div>
      </div>

      {data.nodes.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-500">
          {t("org_chart.create_positions_to_build")}
        </div>
      ) : (
        <div ref={chartRef} className="org-chart overflow-x-auto p-2">
          <ul className="org-root">
            {data.nodes.map((root) => (
              <Node key={root.id} node={root} deptFilter={deptFilter} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
